import React, { useEffect, useRef, useState } from 'react';

interface MonacoEditorProps {
  value: string;
  language?: string;
  onChange?: (value: string) => void;
}

const MonacoEditor: React.FC<MonacoEditorProps> = ({ value, language = 'javascript', onChange }) => {
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<HTMLDivElement>(null);
  const [monacoLoaded, setMonacoLoaded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    // Dynamically import only the required Monaco components
    import('monaco-editor/esm/vs/editor/editor.api').then(monaco => {
      // Configure Monaco Environment for web workers
      (window as any).MonacoEnvironment = {
        getWorkerUrl: function (_moduleId: string, label: string) {
          if (label === 'typescript' || label === 'javascript') {
            return './ts.worker.js';
          }
          if (label === 'json') {
            return './json.worker.js';
          }
          if (label === 'css' || label === 'scss' || label === 'less') {
            return './css.worker.js';
          }
          if (label === 'html' || label === 'xml') {
            return './html.worker.js';
          }
          return './editor.worker.js';
        }
      };
      
      if (isMounted && monacoRef.current) {
        editorRef.current = monaco.editor.create(monacoRef.current, {
          value,
          language,
          theme: 'vs-dark',
          automaticLayout: true,
          minimap: { enabled: true },
          scrollBeyondLastLine: false,
          fontSize: 14,
          lineNumbers: 'on',
          roundedSelection: false,
          cursorStyle: 'line',
          cursorBlinking: 'blink'
        });

        editorRef.current.onDidChangeModelContent(() => {
          onChange?.(editorRef.current.getValue());
        });
        setMonacoLoaded(true);
      }
    }).catch(error => {
      console.error('Failed to load Monaco editor:', error);
    });

    return () => {
      isMounted = false;
      editorRef.current?.dispose();
    };
  }, [language]);

  useEffect(() => {
    if (monacoLoaded && editorRef.current) {
      // Only update if the value is different to prevent unnecessary updates
      const currentValue = editorRef.current.getValue();
      if (currentValue !== value) {
        editorRef.current.setValue(value);
      }
    }
  }, [value, monacoLoaded]);

  return <div ref={monacoRef} className="h-full w-full" />;
};

export default MonacoEditor;